import { t } from '@trimble/i18n';
import { Component, JSX, mergeProps, onMount } from 'solid-js';

export interface ConfirmModusHandle {
  show(): void;
  close(): void;
  getDialog(): HTMLDialogElement | null;
}

interface IConfirmModus {
  id?: string;
  header?: string;
  description?: string;
  children?: JSX.Element;
  showClose?: boolean;
  primaryButtonLabel?: string;
  primaryButtonClass?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'danger';
  secondaryButtonLabel?: string;
  secondaryButtonClass?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'danger';
  onCancel(): void;
  onConfirm(): void;
  modalRef(handle: ConfirmModusHandle): void;
}

export const ConfirmModus: Component<IConfirmModus> = (p: IConfirmModus) => {
  const props = mergeProps(
    {
      id: 'confirm-modus-modal',
      primaryButtonLabel: t('general.yes'),
      secondaryButtonLabel: t('general.no'),
      showClose: true,
    },
    p
  );

  let modalEl: HTMLDialogElement | null;
  let initialized: boolean = false;

  //this must run after the component is hydrated
  const getDialog = (): HTMLDialogElement | null => {
    if (!initialized) {
      const byModalId = document.getElementById(
        props.id
      ) as HTMLDialogElement | null;
      if (byModalId && typeof byModalId.close === 'function') {
        modalEl = byModalId;
      } else {
        const modalWrapper = document.getElementById(`${props.id}-dialog`);
        modalEl =
          modalWrapper?.querySelector('dialog') ??
          modalWrapper?.shadowRoot?.querySelector('dialog') ??
          null;
      }
      initialized = modalEl !== null;
    }
    return modalEl;
  };

  onMount(() => {
    props.modalRef({
      show: () => {
        if (!initialized) getDialog();
        modalEl?.showModal?.();
      },
      close: () => {
        if (!initialized) getDialog();
        modalEl?.close?.();
      },
      getDialog,
    });
  });

  return (
    <>
      <modus-wc-modal
        aria-label="confirm-modal"
        custom-class=""
        fullscreen={false}
        id={props.id ? `${props.id}-dialog` : 'confirm-modus-modal'}
        modal-id={props.id ?? 'confirm-modus-modal'}
        backdrop="default"
        position="center"
        show-close={props.showClose}
        show-fullscreen-toggle="false"
      >
        <span slot="header">{props.header}</span>
        <div slot="content">{props.children ?? props.description}</div>
        <modus-wc-button
          slot="footer"
          id="close-modal-btn-secondary"
          size="md"
          variant="filled"
          color={props.secondaryButtonClass ?? 'secondary'}
          onClick={() => {
            props.onCancel();
            getDialog()?.close();
          }}
        >
          {props.secondaryButtonLabel}
        </modus-wc-button>
        <modus-wc-button
          slot="footer"
          id="close-modal-btn-primary"
          size="md"
          variant="filled"
          color={props.primaryButtonClass ?? 'primary'}
          onClick={() => {
            props.onConfirm();
            getDialog()?.close();
          }}
        >
          {props.primaryButtonLabel}
        </modus-wc-button>
      </modus-wc-modal>
    </>
  );
};
